import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import api from '../api/api';
import NavBar from '../components/navBar';
import NativeAffiliateBanner from '../components/ads/NativeAffiliateBanner';
import useApi from '../hooks/useApi';
import usePageSeo from '../utils/usePageSeo';
import { getLastBlogsListPage } from '../utils/blogsCache';
import {
  normalizeBlogContent,
  parseMarkdownToBlocks,
  renderInline,
} from '../utils/renderMarkdown';

function viewToSearch(view) {
  if (!view || view === 'home' || view === 'dashboard') return '';
  return `?view=${encodeURIComponent(view)}`;
}

function isTokenValid(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000 > Date.now();
  } catch {
    return false;
  }
}

export default function BlogDetailPage() {
  const { slug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { request } = useApi();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);

  const blogsListPath = useMemo(() => {
    const fromState = Number(location.state?.fromPage);
    const page =
      Number.isFinite(fromState) && fromState >= 1
        ? Math.floor(fromState)
        : getLastBlogsListPage();
    return page > 1 ? `/blogs?page=${page}` : '/blogs';
  }, [location.state]);

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    setIsAuthenticated(Boolean(token && isTokenValid(token)));
  }, []);

  const handleNavigate = useCallback(
    (view) => {
      navigate({ pathname: '/', search: viewToSearch(view) });
    },
    [navigate],
  );

  const handleLogout = useCallback(async () => {
    try {
      await request({
        method: 'post',
        url: '/api/v1/users/logout/',
        withAuth: true,
        data: { refresh: localStorage.getItem('refresh_token') },
      });
    } catch (err) {
      console.error('Logout API error:', err);
    }
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user_id');
    localStorage.removeItem('name');
    setIsAuthenticated(false);
    handleNavigate('home');
  }, [request, handleNavigate]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/api/v1/pdf_parser/blogs/${slug}/`);
        setBlog(data?.blog || null);
      } catch (err) {
        console.error('Failed to load blog', err);
        setBlog(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [slug]);

  const bodyContent = useMemo(() => {
    if (!blog?.content) return '';
    return normalizeBlogContent(blog.content, blog.title);
  }, [blog]);

  const blocks = useMemo(() => parseMarkdownToBlocks(bodyContent), [bodyContent]);

  const description = useMemo(() => {
    if (blog?.seo_description) return blog.seo_description;
    if (blog?.excerpt) return blog.excerpt;
    return bodyContent.replace(/^#{1,6}\s+/gm, '').slice(0, 155);
  }, [blog, bodyContent]);

  usePageSeo({
    title: blog?.seo_title || blog?.title || 'Blog',
    description: description || 'Blog article on Poppyruz.',
    path: `/blogs/${slug}`,
    image: blog?.cover_image || undefined,
    jsonLd: blog
      ? {
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: blog.title,
          description: description || undefined,
          datePublished: blog.published_at || undefined,
          image: blog.cover_image || undefined,
          author: { '@type': 'Organization', name: 'Poppyruz' },
          publisher: { '@type': 'Organization', name: 'Poppyruz' },
        }
      : null,
  });

  return (
    <div className="min-h-screen bg-white text-gray-900">
      <NavBar
        isAuthenticated={isAuthenticated}
        currentView="blogs"
        onNavigate={handleNavigate}
        onLogout={handleLogout}
      />
      <main className="mx-auto max-w-3xl px-4 pb-16 pt-24">
        <Link to={blogsListPath} className="text-sm text-violet-900 hover:underline">
          ← Back to blogs
        </Link>

        {loading ? (
          <div className="py-16 text-center text-gray-500">Loading article...</div>
        ) : !blog ? (
          <div className="mt-6 rounded-lg border p-8 text-center text-gray-500">Blog not found.</div>
        ) : (
          <article className="mt-6">
            <h1 className="text-3xl font-bold">{blog.title}</h1>
            {blog.excerpt ? <p className="mt-3 text-gray-600">{blog.excerpt}</p> : null}
            <div className="mt-8 space-y-4 text-gray-800 leading-relaxed">
              {blocks.map((block, i) => {
                if (block.type === 'h2') {
                  return (
                    <h2 key={i} className="pt-2 text-xl font-semibold text-gray-900">
                      {renderInline(block.text)}
                    </h2>
                  );
                }
                if (block.type === 'h3') {
                  return (
                    <h3 key={i} className="pt-1 text-lg font-semibold text-gray-900">
                      {renderInline(block.text)}
                    </h3>
                  );
                }
                return (
                  <p key={i} className="text-base">
                    {renderInline(block.text)}
                  </p>
                );
              })}
            </div>
            {blog.read_path ? (
              <p className="mt-8">
                <Link to={blog.read_path} className="text-violet-900 hover:underline">
                  Read this book on Poppyruz →
                </Link>
              </p>
            ) : null}
            <NativeAffiliateBanner placement="blog_detail" className="mt-12" />
          </article>
        )}
      </main>
    </div>
  );
}
